import { useMemo } from 'react';
import PropTypes from 'prop-types';
import DetailsGrid from '@/common-utils/components/renders/detailsGrid/DetailsGrid';
import Entity from '@/common-utils/components/renders/entity/Entity';
import Icon from '@/common-utils/components/renders/icon/Icon';
import ICONS from '@/common-utils/enums/icons';
import GatewayView from './GatewayView';
import BeneficiaryView from './BeneficiaryView';
import payluddTerminalStateFields from '../fields/payluddTerminalStateFields';
import { buildPayluddMpfFlatData } from '../utils/buildPayluddTerminalStateDisplay';

const PAYLUDD_SOFTWARE_FIELDS = {
    applicationVersion: payluddTerminalStateFields.PAYLUDD.APPLICATION_VERSION,
    payluddVersion: payluddTerminalStateFields.PAYLUDD.PAYLUDD_VERSION,
    androidVersion: payluddTerminalStateFields.PAYLUDD.ANDROID_VERSION,
    firmware: payluddTerminalStateFields.PAYLUDD.FIRMWARE
};

const PAYLUDD_MPF_FIELDS = {
    payluddMpfMode: payluddTerminalStateFields.PAYLUDD_MPF_DISPLAY.MODE,
    payluddMpfPaymentReceiptKeys: payluddTerminalStateFields.PAYLUDD_MPF_DISPLAY.PAYMENT_TICKETS,
    payluddMpfAdditionalServiceKeys: payluddTerminalStateFields.PAYLUDD_MPF_DISPLAY.ADDITIONAL_SERVICES,
    payluddMpfBurgerMenuLock: payluddTerminalStateFields.PAYLUDD_MPF_DISPLAY.BURGER_MENU_LOCK
};

const PAYLUDD_INIT_FIELDS = {
    initializationStatus: payluddTerminalStateFields.PAYLUDD.INITIALIZATION_STATUS,
    packageCode: payluddTerminalStateFields.PAYLUDD.PACKAGE_CODE,
    packageLabel: payluddTerminalStateFields.PAYLUDD.PACKAGE_LABEL,
    initializationDate: payluddTerminalStateFields.PAYLUDD.INITIALIZATION_DATE,
    lastUpdate: payluddTerminalStateFields.PAYLUDD.POS_TERMINAL_STATE_DATE
};

/**
 * Payludd terminal state details: software / MPF / init grids + monetic configuration.
 * @param {object} root0
 * @param {object} [root0.posTerminal]
 * @param {boolean} [root0.hidePayluddData] PACE mode: show field labels without Payludd data
 * @returns {JSX.Element}
 */
function PayluddPosTerminalDetails({ posTerminal, hidePayluddData = false }) {
    const displayPosTerminal = hidePayluddData ? {} : posTerminal;
    const payluddMpfGridData = useMemo(() => buildPayluddMpfFlatData(displayPosTerminal), [displayPosTerminal]);
    const beneficiaries = hidePayluddData? []: (posTerminal?.payludd?.paymentConfiguration?.beneficiaries ?? []);
    const gateways = hidePayluddData ? [] : (posTerminal?.payludd?.paymentConfiguration?.gateways ?? []);
    return (
        <div className='space-y-4'>
            {(hidePayluddData || posTerminal) &&
                <div className='grid grid-cols-1 lg:grid-cols-3 gap-x-8 gap-y-4'>
                    <div className='min-w-0'>
                        <DetailsGrid data={displayPosTerminal} fields={PAYLUDD_SOFTWARE_FIELDS} singleColumnLayout />
                    </div>
                    <div className='min-w-0'>
                        <DetailsGrid data={payluddMpfGridData} fields={PAYLUDD_MPF_FIELDS} singleColumnLayout />
                    </div>
                    <div className='min-w-0'>
                        <DetailsGrid data={displayPosTerminal} fields={PAYLUDD_INIT_FIELDS} singleColumnLayout />
                    </div>
                </div>}
            <div className='space-y-3'>
                <div className='flex items-center gap-2 text-lg font-semibold opacity-80'>
                    <Icon icon={ICONS.MONETIZATION} />
                    <Entity entity='page.payludd.customization.accordions.monetic_settings' />
                </div>
                <GatewayView gateways={gateways} />
                <BeneficiaryView beneficiaries={beneficiaries} />
            </div>
        </div>
    );
}

PayluddPosTerminalDetails.propTypes = {
    posTerminal: PropTypes.object,
    hidePayluddData: PropTypes.bool
};

export default PayluddPosTerminalDetails;
